import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {RepoIcon} from '@primer/octicons-react'
import {Checkbox, Link, Spinner} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {memo, useEffect, useRef} from 'react'

import {ItemType} from '../../api/memex-items/item-type'
import {getInitialState} from '../../helpers/initial-state'
import {Resources} from '../../strings'
import {Blankslate} from '../common/blankslate'
import {useBulkAddItems} from './bulk-add/bulk-add-items-provider'
import styles from './item-suggestions-list.module.css'
import {SuggestedItem} from './suggested-item'

const NoSuggestedItems = memo(function NoSuggestedItems({hasSearchInput}: {hasSearchInput: boolean}) {
  return (
    <Blankslate {...testIdProps('side-panel-no-suggested-items')} className={styles.Blankslate}>
      <Octicon icon={RepoIcon} size={30} className={styles.Octicon} />
      <h2>No items to add</h2>
      <p className={styles.Octicon}>{hasSearchInput ? Resources.noItemsInSearchResult : Resources.repoIsEmpty}</p>
    </Blankslate>
  )
})

export const NoSuggestedRepos = memo(function NoSuggestedRepos({
  isOrganization,
  owner,
}: {
  isOrganization: boolean
  owner?: string
}) {
  return (
    <Blankslate {...testIdProps('side-panel-no-suggested-repos')} className={styles.Blankslate}>
      <Octicon icon={RepoIcon} size={30} className={styles.Octicon} />
      <h2>No repositories found</h2>
      <p className={styles.Octicon}>
        Create a{' '}
        <Link inline href={isOrganization ? `/orgs/${owner}/repositories` : `/${owner}?tab=repositories`}>
          repository
        </Link>{' '}
        to get started.
      </p>
    </Blankslate>
  )
})

const Loader = memo(function Loader() {
  return (
    <Blankslate className={styles.Blankslate}>
      <Spinner />
    </Blankslate>
  )
})

export const ItemSuggestionsList = memo(function ItemSuggestionsList() {
  const {
    selectedRepo,
    items,
    loading,
    finishedFetchingRepoItems,
    selectAllItems,
    areAllItemsSelected,
    searchQuery,
    MAX_ITEM_NUM,
  } = useBulkAddItems()

  const {projectOwner, isOrganization} = getInitialState()
  const owner = projectOwner?.name?.toLowerCase()
  const searchResultsRef = useRef<HTMLDivElement>(null)
  const ariaLiveRef = useRef<HTMLDivElement>(null)
  const previousSearch = useRef<string | undefined>(searchQuery)
  const previousRepo = useRef<typeof selectedRepo>(selectedRepo)
  const searchAttemptCount = useRef<number>(0)
  const announcedAttemptCount = useRef<number>(-1)

  // Helper function to announce search results
  const announceResults = () => {
    if (searchResultsRef.current && ariaLiveRef.current) {
      announce(searchResultsRef.current.textContent || '', {element: ariaLiveRef.current})
      announcedAttemptCount.current = searchAttemptCount.current
    }
  }

  // triggered on search submit or repo change
  useEffect(() => {
    // track search query
    if (previousSearch.current !== searchQuery) {
      previousSearch.current = searchQuery
      searchAttemptCount.current += 1
    }
    // Track repository changes
    if (previousRepo.current !== selectedRepo) {
      previousRepo.current = selectedRepo
      searchAttemptCount.current += 1
    }
  }, [searchQuery, selectedRepo])

  useEffect(() => {
    // checks that search is complete and that it's a new search
    if (loading || !finishedFetchingRepoItems) return
    if (searchAttemptCount.current === announcedAttemptCount.current) return

    const timeoutId = setTimeout(() => {
      announceResults()
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [loading, finishedFetchingRepoItems])

  return (
    <div className={styles.Box} {...testIdProps('side-panel-suggested-items')}>
      <div role="status" aria-live="polite" ref={ariaLiveRef} className="sr-only" />
      {loading ? (
        <Loader />
      ) : items?.length === 0 && finishedFetchingRepoItems && selectedRepo ? (
        <div ref={searchResultsRef}>
          <NoSuggestedItems hasSearchInput={!!searchQuery} />
        </div>
      ) : selectedRepo ? (
        <div>
          <div {...testIdProps('selection-all-items')} className={styles.Box_1}>
            <div className={styles.Box_2}>
              <label className={styles.Box_3}>
                <Checkbox
                  aria-label={areAllItemsSelected ? 'Deselect all items' : 'Select all items'}
                  checked={areAllItemsSelected}
                  onChange={selectAllItems}
                  className={styles.Checkbox}
                />
              </label>
              <span>{areAllItemsSelected ? 'Deselect all items' : 'Select all items'}</span>
            </div>
            {items?.length === MAX_ITEM_NUM && (
              <span {...testIdProps('side-panel-suggested-items-recent-count')} className={styles.Text}>
                Showing {MAX_ITEM_NUM} most recent items
              </span>
            )}
          </div>
          <ul>
            {items?.map(suggestedItem => {
              const repoName = selectedRepo.nameWithOwner
              return (
                <SuggestedItem
                  item={suggestedItem}
                  // key is a reserved name in react and thus it is not passing this prop further
                  // so we need a key for this render method and a keyName to pass it down to the children
                  key={`${selectedRepo.nameWithOwner}-${suggestedItem.number}`}
                  keyName={`${selectedRepo.nameWithOwner}-${suggestedItem.number}`}
                  url={
                    suggestedItem.type === ItemType.PullRequest
                      ? `/${repoName}/pull/${suggestedItem.number}`
                      : `/${repoName}/issues/${suggestedItem.number}`
                  }
                />
              )
            })}
          </ul>
          <span {...testIdProps('side-panel-suggested-items-footer')} ref={searchResultsRef} className={styles.Text_1}>
            Showing {items?.length} most recent {items?.length === 1 ? 'item' : 'items'} that{' '}
            {items?.length === 1 ? 'has' : 'have'} not been added to this project
            {items && items?.length > 1 && '. Use search to narrow down this list.'}
          </span>
        </div>
      ) : (
        <NoSuggestedRepos owner={owner} isOrganization={isOrganization} />
      )}
    </div>
  )
})

try{ NoSuggestedItems.displayName ||= 'NoSuggestedItems' } catch {}
try{ NoSuggestedRepos.displayName ||= 'NoSuggestedRepos' } catch {}
try{ Loader.displayName ||= 'Loader' } catch {}
try{ ItemSuggestionsList.displayName ||= 'ItemSuggestionsList' } catch {}